// Installing Wahala as an app (WEB-10): the browser's own install offer, caught once at boot and kept
// for whoever asks (the install bar, the Menu's Get the app, the website's Get the app link), and the
// way to install on a browser that never offers one. Never inside the Lagos Life frame, never once the
// app is installed.
//
// - prompt: the browser offered an install (beforeinstallprompt): our button opens its dialog.
// - ios: Safari on an iPhone or iPad: Share, then Add to Home Screen.
// - iosOther: another browser on an iPhone or iPad: the same from its share menu, or open in Safari.
// - inapp: a browser inside another app (WhatsApp, Facebook, Instagram): open the page in the phone's own
//   browser first.
// - manual: a browser that offered nothing (yet): its own menu holds Install app or Add to Home screen.
// - null: inside the Lagos Life frame, or installed already.
//
// Counted for real, per player (POST /api/v1/me/app): the browser saying it installed the app, and the
// first open from the home screen on this phone (the only sign an iPhone gives). Each sign waits on
// the phone until the player is signed in, then goes once. It feeds the park office's Marketing
// screen and the "Installed the app" ad goal.
import { isFramed } from '@wahala/vatar-client';
import { useSyncExternalStore } from 'react';
import { api, loadSession } from './api';

export type InstallWay = 'prompt' | 'ios' | 'iosOther' | 'inapp' | 'manual';

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let offer: InstallPrompt | null = null;
let installed = false;
let started = false;
const listeners = new Set<() => void>();
const changed = () => {
  for (const f of listeners) f();
};

/** Safari itself on an iPhone or iPad (iPadOS says it is a Mac with a touch screen), not a webview. */
export function isIosSafari(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  const ua = nav.userAgent;
  return isIos(nav) && /Version\/[\d.]+.*Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua);
}

export function isIos(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  const ua = nav.userAgent;
  return /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && nav.maxTouchPoints > 1);
}

/** A browser inside another app: WhatsApp, Facebook, Instagram, an Android webview. */
export function isInApp(ua: string = navigator.userAgent): boolean {
  return /FBAN|FBAV|Instagram|WhatsApp|Line\/|Snapchat|TikTok|; wv\)/.test(ua);
}

function standalone(): boolean {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    // no matchMedia: carry on
  }
  return Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

/** Called once at boot, before anything asks: the browser's offer comes early and only once. */
export function startInstall(): void {
  if (started) return;
  started = true;
  installed = standalone();
  // opened from the home screen: the first time on this phone is a sign the app is installed
  if (installed && !isFramed() && !flag(LAUNCHED)) addPending('launched');
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // we ask in our own bar and sheet, at our own time
    offer = e as InstallPrompt;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    offer = null;
    installed = true;
    changed();
    addPending('installed');
    void reportInstalls();
  });
}

/** How this phone installs Wahala now, or null when it can't or already has. */
export function installWay(): InstallWay | null {
  if (isFramed() || installed) return null;
  if (offer) return 'prompt';
  if (isInApp()) return 'inapp';
  if (isIos()) return isIosSafari() ? 'ios' : 'iosOther';
  return 'manual';
}

/** Open the browser's install dialog (only from a tap). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const o = offer;
  if (!o) return 'unavailable';
  try {
    await o.prompt();
    const choice = o.userChoice ? (await o.userChoice).outcome : 'accepted';
    // an offer is good for one dialog; the browser sends a fresh one if it will ask again
    offer = null;
    if (choice === 'accepted') installed = true;
    changed();
    return choice;
  } catch {
    return 'unavailable';
  }
}

function subscribe(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** The install way, kept current for React (an offer can arrive after the screen draws). */
export function useInstallWay(): InstallWay | null {
  return useSyncExternalStore(subscribe, installWay, () => null);
}

let linked = false;

/**
 * Called once at boot: ?install=1 (the website's Get the app) asks for the install sheet, and is
 * taken out of the address bar so a reload never asks again.
 */
export function takeInstallLink(): void {
  const q = new URLSearchParams(location.search);
  if (!q.has('install')) return;
  q.delete('install');
  const rest = q.toString();
  history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  linked = !isFramed();
}

/** The install link, once: the first caller gets true. */
export function claimInstallLink(): boolean {
  const was = linked;
  linked = false;
  return was;
}

// ---------------------------------------------------------------- counted, per player

type Sign = 'installed' | 'launched';
const PENDING = 'wahala.app.pending';
const LAUNCHED = 'wahala.app.launched';

function flag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function pending(): Sign[] {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is Sign => x === 'installed' || x === 'launched') : [];
  } catch {
    return [];
  }
}

function savePending(signs: Sign[]): void {
  try {
    if (signs.length) localStorage.setItem(PENDING, JSON.stringify(signs));
    else localStorage.removeItem(PENDING);
  } catch {
    // storage blocked: this visit only
  }
}

function addPending(sign: Sign): void {
  const now = pending();
  if (!now.includes(sign)) savePending([...now, sign]);
}

/** The phone's kind for the count: nothing more about it. */
export function platformOf(
  nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator,
): 'android' | 'ios' | 'other' {
  if (isIos(nav)) return 'ios';
  return /Android/i.test(nav.userAgent) ? 'android' : 'other';
}

let reporting = false;

/**
 * Send what this phone has waiting, if a player is signed in (called at sign-in and when the browser
 * says it installed). A sign that fails stays for next time; a player the api won't count for (a
 * Lagos Life player) clears it.
 */
export async function reportInstalls(): Promise<void> {
  if (reporting || isFramed() || !loadSession()) return;
  const signs = pending();
  if (!signs.length) return;
  reporting = true;
  try {
    for (const sign of signs) {
      try {
        await api.appInstalled(sign, platformOf());
      } catch (e) {
        if ((e as { status?: number }).status !== 409) continue;
      }
      savePending(pending().filter((s) => s !== sign));
      if (sign === 'launched') {
        try {
          localStorage.setItem(LAUNCHED, '1');
        } catch {
          // storage blocked: it may be sent again, and the api counts it once
        }
      }
    }
  } finally {
    reporting = false;
  }
}
